/**
 * PCG32 — the only source of randomness in the simulation (TRD-00 R-00-02, TRD-01 §3).
 *
 * Why not xorshift32 or `Math.random`:
 *  - `Math.random` is unseeded and engine-specific, so a run could never be replayed.
 *  - A 32-bit generator has a period short enough to matter across a 25-wave run with
 *    thousands of draws, and its low bits are notoriously poor.
 *
 * `BigInt` gives us guaranteed 64-bit wraparound semantics, identical on every engine.
 * It is slower than a 32-bit generator, but the sim draws on the order of 10^4 times per
 * run — this has never been the bottleneck, and exact reproducibility is the whole point.
 *
 * TRD-00 scope implements the core stream. The derived helpers (nextInt with reject
 * sampling, pick, weightedPick, shuffle, chance) belong to TRD-01 §3.
 */

const MASK_64 = (1n << 64n) - 1n;
const MASK_32 = 0xffffffffn;
const MULTIPLIER = 6364136223846793005n;

/** 2^32, as a plain number. Written as a literal because `**` is banned (TRD-00 R-00-04). */
const TWO_POW_32 = 4294967296;

export class Rng {
  private state: bigint;
  private inc: bigint;

  /**
   * @param seed   Any 64-bit value. Daily seeds come from `dailySeed(date)` (TRD-10 §4).
   * @param stream Independent stream selector. Stream 1 is gameplay; stream 2 is the
   *               daily generator, kept separate so a change in how much content
   *               generation consumes cannot shift every shop roll (TRD-13 R-13-04).
   */
  constructor(seed: bigint, stream = 1n) {
    this.inc = ((stream << 1n) | 1n) & MASK_64;
    this.state = 0n;
    this.nextU32();
    this.state = (this.state + (seed & MASK_64)) & MASK_64;
    this.nextU32();
  }

  /** Uniform in [0, 2^32). */
  nextU32(): number {
    const previous = this.state;
    this.state = (previous * MULTIPLIER + this.inc) & MASK_64;

    const xorshifted = Number((((previous >> 18n) ^ previous) >> 27n) & MASK_32);
    const rotation = Number(previous >> 59n);

    return ((xorshifted >>> rotation) | (xorshifted << (-rotation & 31))) >>> 0;
  }

  /** Uniform in [0, 1). */
  nextFloat(): number {
    return this.nextU32() / TWO_POW_32;
  }

  /**
   * Uniform integer in [minInclusive, maxExclusive).
   *
   * Uses rejection sampling rather than `% range`. Modulo introduces a bias toward low
   * values whenever `range` does not divide 2^32 — small, but it applies to *every* shop
   * roll and wave composition, and it would make the daily leaderboard's distribution
   * quietly differ from what balance testing assumed (TRD-01 R-01-03).
   */
  nextInt(minInclusive: number, maxExclusive: number): number {
    const range = maxExclusive - minInclusive;
    if (range <= 1) return minInclusive;

    // Largest multiple of `range` that fits in u32; anything above it is rejected.
    const limit = TWO_POW_32 - (TWO_POW_32 % range);
    let value = this.nextU32();
    while (value >= limit) {
      value = this.nextU32();
    }
    return minInclusive + (value % range);
  }

  /** True with probability `p`. */
  chance(p: number): boolean {
    if (p <= 0) return false;
    if (p >= 1) return true;
    return this.nextFloat() < p;
  }

  /** Uniform element. Throws on an empty array — an empty pool is always a content bug. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Rng.pick: empty array');
    const item = items[this.nextInt(0, items.length)];
    if (item === undefined) throw new RangeError('Rng.pick: index out of range');
    return item;
  }

  /**
   * Weighted element. `weights` must be the same length as `items` and sum to > 0.
   *
   * Accumulates in array order so the result depends only on the inputs and one draw,
   * never on iteration order of a map or object.
   */
  weightedPick<T>(items: readonly T[], weights: readonly number[]): T {
    if (items.length === 0) throw new RangeError('Rng.weightedPick: empty array');
    if (items.length !== weights.length) {
      throw new RangeError('Rng.weightedPick: items and weights differ in length');
    }

    let total = 0;
    for (let i = 0; i < weights.length; i++) {
      const w = weights[i] ?? 0;
      if (w > 0) total += w;
    }
    if (total <= 0) throw new RangeError('Rng.weightedPick: weights sum to zero');

    let roll = this.nextFloat() * total;
    for (let i = 0; i < items.length; i++) {
      const w = weights[i] ?? 0;
      if (w <= 0) continue;
      roll -= w;
      if (roll < 0) {
        const item = items[i];
        if (item !== undefined) return item;
      }
    }

    // Only reachable through floating-point accumulation at the very top of the range.
    const last = items[items.length - 1];
    if (last === undefined) throw new RangeError('Rng.weightedPick: empty array');
    return last;
  }

  /** Fisher–Yates, in place. Draw order is fixed, so the permutation is reproducible. */
  shuffle<T>(items: T[]): void {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i + 1);
      const a = items[i] as T;
      const b = items[j] as T;
      items[i] = b;
      items[j] = a;
    }
  }

  /**
   * Stream position, folded into the world hash (TRD-01 R-01-13).
   *
   * Including this is what makes the hash catch a divergence in *how many* draws were
   * taken, not just in the resulting entity state. Two runs that happen to land on the
   * same positions via different draw counts are still caught.
   */
  get position(): bigint {
    return this.state;
  }

  /**
   * Exact copy, for speculative evaluation that must not disturb the live stream —
   * `previewPlacement` needs this to answer "what would this tower do?" without
   * consuming a draw (TRD-08 R-08-15).
   */
  clone(): Rng {
    return Rng.fromRaw(this.state, this.inc);
  }

  /** The stream selector, needed to restore a stream exactly. */
  get increment(): bigint {
    return this.inc;
  }

  /** Restore a stream from serialized raw values. */
  static fromRaw(state: bigint, inc: bigint): Rng {
    const rng = new Rng(0n);
    rng.state = state & MASK_64;
    rng.inc = inc & MASK_64;
    return rng;
  }
}
